import * as React from "react";
import { FormGroup, HelpBlock } from "react-bootstrap";
import FormFieldDescription from "./components/FormFieldDescription";
import ErrorBlock, { ErrorFieldType } from "./components/ErrorBlock";

interface InputFieldProps {
  autoCapitalize?: "off" | "on";
  className?: string;
  disabled?: boolean;
  disableErrors?: boolean;
  flexDirection?: any;
  helpBlockPosition?: string;
  input: {
    name: string;
    value: any;
    onBlur(value: any): void;
    onFocus(): void;
    onChange(value: any): void;
  };
  label?: string;
  labelClassName?: string;
  fieldsAsColumns?: boolean;
  formCtrlClassName?: string;
  helpText?: string;
  meta: {
    invalid: boolean;
    error: boolean;
    touched: boolean;
    dirty: boolean;
  };
  placeholder?: string;
  successMessage?: {
    isValid: boolean;
    message: string;
  };
  type?: string;
  wrapperClassNames?: string;
  maxlength?: number;
  autosave?: boolean;
  onKeyDown?(event: any): void;
  formatter?(value: any): void;
}

const InputField: React.StatelessComponent<InputFieldProps> = ({
  autoCapitalize,
  className,
  disableErrors,
  flexDirection,
  formatter,
  formCtrlClassName,
  helpBlockPosition,
  label,
  labelClassName,
  wrapperClassNames,
  input,
  disabled,
  placeholder,
  helpText,
  meta,
  type,
  maxlength,
  successMessage,
  onKeyDown,
  autosave,
}) => {
  let usedClassNames = wrapperClassNames
    ? `${wrapperClassNames} formfield`
    : "formfield";
  usedClassNames = disabled ? `${usedClassNames} disabled` : usedClassNames;

  const inputClassNames = className
    ? `form-control ${className} `
    : "form-control";

  const usedLabelClassName = labelClassName
    ? `formlabel ${labelClassName}`
    : "formlabel";

  let blockTop;
  let blockBottom: any = null;
  const showLabel = { display: label ? "flex" : "none" };
  const displayedLabel = (
    <label style={showLabel} className={usedLabelClassName}>
      {label}
    </label>
  );

  if (helpBlockPosition === "top") {
    blockTop = (
      <ErrorBlock
        fieldType={ErrorFieldType.SINGLE}
        meta={meta}
        fallbackCmp={displayedLabel}
      />
    );
  } else {
    blockTop = displayedLabel;
  }

  if (helpBlockPosition === "bottom" || !helpBlockPosition) {
    if (!disableErrors && meta.error) {
      blockBottom = (
        <ErrorBlock fieldType={ErrorFieldType.SINGLE} meta={meta} />
      );
    } else if (
      !meta.error &&
      successMessage &&
      successMessage.isValid &&
      successMessage.message
    ) {
      blockBottom = <HelpBlock>{successMessage.message}</HelpBlock>;
    }
  }

  const activeAutoCapitalize = "on" || autoCapitalize;

  const handleChange = (event) =>
    typeof formatter === "function"
      ? input.onChange(formatter(event.target.value))
      : input.onChange(event.target.value);

  return (
    <FormGroup
      controlId={placeholder}
      style={{ flexDirection: flexDirection || "row" }}
      validationState={
        meta.invalid && meta.error && (meta.touched || meta.dirty)
          ? "error"
          : "success"
      }
      className={formCtrlClassName}
    >
      <div className="fieldlabel">
        {blockTop}
        <FormFieldDescription>{helpText}</FormFieldDescription>
      </div>
      <div className={usedClassNames}>
        <input
          autoCapitalize={activeAutoCapitalize}
          autoCorrect="off"
          spellCheck={false}
          className={inputClassNames}
          disabled={disabled}
          placeholder={placeholder}
          type={type ? type : "text"}
          onBlur={() => {
            input.onBlur(input.value);
            if (autosave) {
              // Event for PartFormWrapper, update the field value right away
              const fieldUpdateEvent = new CustomEvent("updateSingleField", {
                detail: { fieldName: input.name, value: input.value },
              });
              window.dispatchEvent(fieldUpdateEvent);
            }
          }}
          onChange={handleChange}
          onFocus={input.onFocus}
          onKeyDown={typeof onKeyDown === "function" ? onKeyDown : undefined}
          value={input.value}
          maxLength={maxlength}
        />
        {blockBottom}
      </div>
    </FormGroup>
  );
};

InputField.defaultProps = {
  successMessage: {},
} as Partial<InputFieldProps>;

export default InputField;
